/**
 * Help & Processes — in-app user guide rendered from markdown files.
 *
 * Content lives in `src/content/help/*.md` for easy editing.
 * Uses react-markdown + remark-gfm for rendering.
 */

import { makeStyles, tokens, Title2, Subtitle2, Divider, Text } from '@fluentui/react-components'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { QuestionCircle24Regular, Briefcase24Regular, People24Regular, Settings24Regular, Rocket24Regular, Bot24Regular, Mail24Regular } from '@fluentui/react-icons'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { useWhatYouHear, whatYouHearMarkdown } from '../hooks/useWhatYouHear'

import gettingStartedMd from '../content/help/01-getting-started.md?raw'
import talentWorkflowsMd from '../content/help/02-talent-workflows.md?raw'
import councilWorkflowsMd from '../content/help/03-council-workflows.md?raw'
import processReferenceMd from '../content/help/04-process-reference.md?raw'
import transitionMd from '../content/help/05-transition-from-sharepoint.md?raw'
import aiAccessMd from '../content/help/06-ai-access.md?raw'

const useStyles = makeStyles({
  root: {
    maxWidth: '820px',
    margin: '0 auto',
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalL}`,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalS,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalL,
  },
  markdown: {
    lineHeight: '1.7',
    color: tokens.colorNeutralForeground2,
    fontSize: '14px',
    // Screenshots are captured at 1440px (#1231) and would otherwise set the
    // column width. Bounded and framed so they read as illustrations of the
    // paragraph above rather than as part of the page's own chrome.
    '& img': {
      display: 'block',
      maxWidth: '100%',
      height: 'auto',
      borderRadius: tokens.borderRadiusMedium,
      border: `1px solid ${tokens.colorNeutralStroke2}`,
      marginTop: tokens.spacingVerticalM,
      marginBottom: tokens.spacingVerticalM,
    },
    '& h1': {
      fontSize: '18px',
      fontWeight: 600,
      marginTop: '16px',
      marginBottom: '8px',
      color: tokens.colorNeutralForeground1,
    },
    '& h2': {
      fontSize: '15px',
      fontWeight: 600,
      marginTop: '20px',
      marginBottom: '6px',
      color: tokens.colorNeutralForeground1,
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '13px',
      marginTop: '8px',
      marginBottom: '12px',
    },
    '& th': {
      textAlign: 'left',
      padding: '8px 12px',
      borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
      fontWeight: 600,
    },
    '& td': {
      padding: '8px 12px',
      borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    '& ol, & ul': {
      paddingLeft: '20px',
      lineHeight: '2',
    },
    '& p': {
      marginBottom: '8px',
    },
  },
  contact: {
    marginTop: tokens.spacingVerticalXL,
    padding: '16px',
    background: tokens.colorNeutralBackground3,
    borderRadius: '8px',
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    textAlign: 'center',
  },
})

interface HelpSectionProps {
  id?: string
  icon: React.ReactNode
  title: string
  content: string
}

// Files under /ai-access/ are downloads (the MCP server, its requirements, the
// skill); without `download` the browser would open them as text.
const markdownComponents = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a href={href} download={href?.startsWith('/ai-access/') ? '' : undefined}>
      {children}
    </a>
  ),
}

/** Generated from the notification catalogue, so it cannot drift from what is sent. */
function WhatYouHearSection() {
  const { data, isError } = useWhatYouHear()
  const content = data
    ? whatYouHearMarkdown(data)
    : isError
      ? 'This list could not be loaded. Try again later.'
      : 'Loading…'
  return (
    <HelpSection
      id="notifications"
      icon={<Mail24Regular />}
      title="What you'll hear from us"
      content={content}
    />
  )
}

function HelpSection({ id, icon, title, content }: HelpSectionProps) {
  const styles = useStyles()
  return (
    <div className={styles.section} id={id}>
      <div className={styles.sectionTitle}>
        {icon}
        <Subtitle2>{title}</Subtitle2>
      </div>
      <div className={styles.markdown}>
        <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {content}
        </Markdown>
      </div>
    </div>
  )
}

export function Help() {
  useDocumentTitle('Help & processes')
  const styles = useStyles()
  const { hash } = useLocation()
  // Deep links such as /help#ai-access from My profile.
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView()
  }, [hash])

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <QuestionCircle24Regular />
        <Title2>Help &amp; Processes</Title2>
      </div>

      <HelpSection id="getting-started" icon={<Rocket24Regular />} title="Getting Started" content={gettingStartedMd} />
      <Divider />
      <HelpSection id="talent-workflows" icon={<Briefcase24Regular />} title="Talent Workflows" content={talentWorkflowsMd} />
      <Divider />
      <HelpSection id="council-workflows" icon={<People24Regular />} title="Council Workflows" content={councilWorkflowsMd} />
      <Divider />
      <HelpSection icon={<Settings24Regular />} title="Process Reference" content={processReferenceMd} />
      <Divider />
      <WhatYouHearSection />
      <Divider />
      <HelpSection icon={<Rocket24Regular />} title="Transitioning from SharePoint" content={transitionMd} />
      <Divider />
      <HelpSection id="ai-access" icon={<Bot24Regular />} title="AI access" content={aiAccessMd} />

      <div className={styles.contact}>
        <Text>
          Still need help? Contact the platform team: <strong>siqi.zhang@bayer.com</strong>
        </Text>
      </div>
    </div>
  )
}
